import React, { useMemo, useState } from 'react'
import { AlertTriangle, Check, CheckCircle2, Clock, History, Loader2, MapPin, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { vehicleControlCenterService } from '../../services/vehicleControlCenterService'
import type { ControlCenterAlert } from '../../services/vehicleControlCenterTypes'

interface Props {
  alerts: ControlCenterAlert[]
  loading?: boolean
  error?: string | null
  onRefresh: () => Promise<void> | void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const VehicleAlertsTab: React.FC<Props> = ({ alerts, loading, error, onRefresh, showMessage }) => {
  const { t } = useTranslation()
  const [view, setView] = useState<'active' | 'history'>('active')
  const [busyId, setBusyId] = useState<number | null>(null)
  const visible = useMemo(() => alerts.filter(alert =>
    view === 'active' ? ['ACTIVE', 'ACKNOWLEDGED'].includes(alert.status) : !['ACTIVE', 'ACKNOWLEDGED'].includes(alert.status)
  ), [alerts, view])

  const transition = async (alert: ControlCenterAlert, action: 'acknowledge' | 'resolve') => {
    setBusyId(alert.id)
    try {
      await vehicleControlCenterService.transitionAlert(alert.id, action)
      showMessage?.(action === 'acknowledge'
        ? t('vcc_alert_acknowledged', { defaultValue: 'Alert acknowledged' })
        : t('vcc_alert_resolved', { defaultValue: 'Alert resolved' }), 'success')
      await onRefresh()
    } catch (e: any) {
      showMessage?.(e?.response?.data?.error || t('vcc_alert_action_failed', { defaultValue: 'Alert action failed' }), 'error')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) return <div className="flex h-56 items-center justify-center gap-2 text-gray-500"><Loader2 className="animate-spin" />{t('vcc_loading', { defaultValue: 'Loading control center…' })}</div>
  if (error) return <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-center text-red-800"><p>{error}</p><button onClick={() => onRefresh()} className="mt-3 rounded-lg bg-red-700 px-3 py-2 text-sm text-white">{t('retry', { defaultValue: 'Retry' })}</button></div>

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('vcc_vehicle_alerts', { defaultValue: 'Vehicle Alerts' })}</h3>
          <p className="text-sm text-gray-500">{t('vcc_alerts_hint', { defaultValue: 'Review and act on alerts raised for this vehicle.' })}</p>
        </div>
        <div className="flex rounded-lg bg-gray-100 p-1">
          <button onClick={() => setView('active')} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${view === 'active' ? 'bg-white font-semibold text-red-700 shadow-sm' : 'text-gray-600'}`}><AlertTriangle size={15} />{t('active', { defaultValue: 'Active' })}</button>
          <button onClick={() => setView('history')} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${view === 'history' ? 'bg-white font-semibold text-gray-900 shadow-sm' : 'text-gray-600'}`}><History size={15} />{t('history', { defaultValue: 'History' })}</button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 py-14 text-center text-gray-500">
          <CheckCircle2 className="mx-auto mb-3 text-emerald-300" size={40} />
          <p className="font-medium text-gray-700">{view === 'active' ? t('vcc_no_active_alerts', { defaultValue: 'No active alerts for this vehicle' }) : t('vcc_no_alert_history', { defaultValue: 'No resolved alert history' })}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(alert => {
            const zone = alert.zone_name || alert.geofence_name || alert.corridor_name
            return (
              <article key={alert.id} className={`rounded-xl border p-4 ${alert.severity === 'CRITICAL' && alert.status === 'ACTIVE' ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-start gap-3">
                  <div className={`rounded-lg p-2 ${alert.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}><AlertTriangle size={18} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{alert.alert_type.replace(/_/g, ' ')}</h4>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${alert.status === 'ACTIVE' ? 'bg-red-600 text-white' : alert.status === 'ACKNOWLEDGED' ? 'bg-amber-500 text-white' : 'bg-gray-500 text-white'}`}>{alert.status}</span>
                      <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-bold text-gray-700">{alert.severity || 'WARNING'}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-700">{alert.message}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Clock size={13} />{new Date(alert.created_at).toLocaleString()}</span>
                      {alert.rule_name && <span className="flex items-center gap-1"><ShieldCheck size={13} />{alert.rule_name}</span>}
                      {zone && <span className="flex items-center gap-1"><MapPin size={13} />{zone}</span>}
                    </div>
                  </div>
                  {view === 'active' && (
                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                      {alert.status === 'ACTIVE' && alert.can_acknowledge !== false && <button disabled={busyId === alert.id} onClick={() => transition(alert, 'acknowledge')} className="flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 disabled:opacity-50"><Check size={14} />{t('vcc_acknowledge', { defaultValue: 'Acknowledge' })}</button>}
                      {alert.can_resolve !== false && <button disabled={busyId === alert.id} onClick={() => transition(alert, 'resolve')} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 size={14} />{t('vcc_resolve', { defaultValue: 'Resolve' })}</button>}
                    </div>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default VehicleAlertsTab
